import { useEffect } from "react";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { DEFAULT_PROJECTS } from "@/lib/portfolio-data";
import { trackCaseView } from "@/lib/analytics";
import BreadcrumbTrail from "@/components/BreadcrumbTrail";

const FieldBlock = ({ label, children, inverted = false }) => (
  <section className={inverted ? "bg-[#0a0a0a] text-white p-6 brutal-border" : "bg-white p-6 brutal-border"}>
    <p className={`font-mono text-xs uppercase tracking-widest mb-3 ${inverted ? "text-[#ff6b35]" : "text-[#ff6b35]"}`}>
      {label}
    </p>
    <p className={`text-base sm:text-lg leading-snug ${inverted ? "text-white/75" : "text-[#0a0a0a]/75"}`}>
      {children}
    </p>
  </section>
);

const ProjectCasePage = () => {
  const { slug } = useParams();
  const project = DEFAULT_PROJECTS.find((item) => item.slug === slug);

  useEffect(() => {
    if (project) trackCaseView(project.id);
  }, [project]);

  if (!project) return <Navigate to="/projetos" replace />;

  return (
    <main id="main-content" className="bg-[#f8f5ee] pt-24">
      <section className="relative overflow-hidden bg-[#ff6b35] noise py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative">
          <BreadcrumbTrail items={[{ label: "Projetos", href: "/projetos" }, { label: project.title }]} />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(320px,0.7fr)] gap-8 items-end">
            <div>
              <div className="inline-block bg-[#0a0a0a] text-white px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-5">
                Case study
              </div>
              <h1 className="font-display text-[#0a0a0a] text-[2.65rem] min-[380px]:text-5xl sm:text-7xl lg:text-8xl uppercase leading-[0.9]">
                {project.title}
              </h1>
              <p className="mt-6 max-w-3xl text-[#0a0a0a]/75 text-lg sm:text-xl leading-snug">
                {project.description}
              </p>
            </div>

            <aside className="bg-white brutal-card p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
                Cliente
              </p>
              <p className="font-display text-[#0a0a0a] text-4xl uppercase leading-none">
                {project.client}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {project.tags.map((tag) => (
                  <span key={tag} className="bg-[#ff6b35] text-[#0a0a0a] brutal-border px-3 py-1 font-mono text-[10px] uppercase tracking-widest">
                    {tag}
                  </span>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#f8f5ee]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <FieldBlock label="Contexto">{project.context}</FieldBlock>
          <FieldBlock label="Problema">{project.problem}</FieldBlock>
          <FieldBlock label="Objetivo" inverted>{project.objective}</FieldBlock>
          <FieldBlock label="Solução" inverted>{project.solution}</FieldBlock>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] gap-8 items-start">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
              Resultado
            </p>
            <h2 className="font-display text-[#0a0a0a] text-4xl sm:text-6xl uppercase leading-none">
              Prova sem números inventados.
            </h2>
            <p className="mt-5 text-[#0a0a0a]/75 text-lg leading-snug">
              {project.result}
            </p>
            {project.metrics.length === 0 && (
              <p className="mt-5 bg-[#f8f5ee] brutal-border px-4 py-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                Métricas públicas ainda não disponíveis.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <section className="bg-[#ff6b35] brutal-card p-6">
              <h3 className="font-display text-[#0a0a0a] text-3xl uppercase leading-none mb-5">
                Tecnologias
              </h3>
              <ul className="space-y-3">
                {project.technologies.map((item) => (
                  <li key={item} className="flex gap-3 text-[#0a0a0a]/80 leading-snug">
                    <CheckCircle2 size={18} className="shrink-0 mt-1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="bg-[#0a0a0a] brutal-card p-6">
              <h3 className="font-display text-white text-3xl uppercase leading-none mb-5">
                Funcionalidades
              </h3>
              <ul className="space-y-3">
                {project.functionalities.map((item) => (
                  <li key={item} className="flex gap-3 text-white/75 leading-snug">
                    <CheckCircle2 size={18} className="shrink-0 mt-1 text-[#ff6b35]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-[#0a0a0a]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8 items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-[#ff6b35] mb-3">
              Próximo passo
            </p>
            <h2 className="font-display text-white text-4xl sm:text-6xl uppercase leading-none">
              Quer transformar um problema parecido?
            </h2>
          </div>
          <div className="flex flex-col gap-4">
            <Link to="/diagnostico-digital" className="btn-brutal btn-brutal-orange">
              <span>Pedir diagnóstico</span>
              <ArrowRight size={18} className="shrink-0" />
            </Link>
            <a href={project.url} target="_blank" rel="noopener noreferrer" className="btn-brutal">
              <span>Ver site publicado</span>
              <ArrowUpRight size={18} className="shrink-0" />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProjectCasePage;
